/**
 * The consent mirror cookie: everything either SIDE needs in order to read it, and nothing else.
 *
 * Deliberately separate from `usercentrics.ts`. That module is browser-only (it reaches for `window`,
 * `document` and `localStorage`, and augments the `Window` type), and importing it from a `.server.ts` file
 * took SSR down with a 500 on every route. Reading the cookie is string and JSON work, so it lives here
 * where both sides can import it safely — `server-functions-shared.server.ts` on the server and
 * `router.tsx`, which is bundled and executed on BOTH, in the browser. The only browser global reached for
 * here is `document.cookie`, guarded, inside a function: nothing runs at import on either side.
 *
 * Writing the cookie stays in `usercentrics.ts`, because deriving what to write means reading the CMP's own
 * storage, which only exists in a browser.
 */

/** A decision the donor has actually made, split by outcome. Absent entirely when nothing is stored yet. */
export interface UsercentricsDecision {
	readonly granted: readonly string[];
	readonly denied: readonly string[];
}

/**
 * Cookie mirroring the CMP's decision so the SERVER can render gated content correctly on the first pass.
 *
 * The CMP keeps its record in `localStorage`, which SSR cannot see, so the server would otherwise render
 * every gated embed as "not decided" and the client would visibly correct it a moment later. No amount of
 * loading the CMP earlier fixes that: the problem is what the *server* knows, not when the CMP arrives.
 *
 * Only a mirror, never the source of truth — the client re-derives it from the CMP on every load and every
 * consent change (see `syncConsentCookie`), so a stale value self-heals on the next render.
 */
export const CONSENT_COOKIE_NAME = 'wc-consent';

/** Bump when the payload shape changes; an unrecognised version is treated as no cookie at all. */
export const CONSENT_COOKIE_VERSION = 1;

/**
 * Under a year, so the mirror cannot outlive the consent it reflects by much even if the client never gets
 * another chance to refresh it.
 */
export const CONSENT_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

/**
 * Parses the mirror cookie. Takes the raw cookie VALUE, so it works the same on the server (from
 * `getCookie`) and in the browser (from `document.cookie`). Returns `undefined` for anything it does not
 * recognise, which callers must treat as "no answer yet" rather than as a denial.
 */
export const parseConsentCookie = (value: string | undefined | null): UsercentricsDecision | undefined => {
	if (!value) return undefined;
	try {
		const parsed: unknown = JSON.parse(decodeURIComponent(value));
		if (typeof parsed !== 'object' || parsed === null) return undefined;
		if (!('v' in parsed) || parsed.v !== CONSENT_COOKIE_VERSION) return undefined;
		if (!('g' in parsed) || !('d' in parsed)) return undefined;
		const { g, d } = parsed;
		if (!Array.isArray(g) || !Array.isArray(d)) return undefined;
		const granted = g.filter((name): name is string => typeof name === 'string');
		const denied = d.filter((name): name is string => typeof name === 'string');
		if (granted.length === 0 && denied.length === 0) return undefined;
		return { granted, denied };
	} catch {
		return undefined;
	}
};

/**
 * Reads the mirror cookie in the browser, for the client half of the render that the server does with
 * `getCookie`. Both sides therefore answer from the same string, which is what makes the first client
 * render agree with the markup it is hydrating.
 */
export const readConsentCookie = (): UsercentricsDecision | undefined => {
	if (typeof document === 'undefined') return undefined;
	const match = document.cookie.split('; ').find((entry) => entry.startsWith(`${CONSENT_COOKIE_NAME}=`));
	return parseConsentCookie(match?.slice(CONSENT_COOKIE_NAME.length + 1));
};
